import { OrderTypeEnum } from '@flipster/apis/types';

export type TpSlType = 'TP' | 'SL';

/**
 * Label variant used on TP/SL chart markers.
 * The line adapter renders "{label} | {qty}" — `qty` is passed separately via
 * `setQuantity`, so only the `{label}` half is built here.
 * - `full` (web): `"{TP,SL} Trigger {price} / {Market,Limit}"`
 * - `short` (webview): `"{TP,SL} {Market,Limit}"`
 */
export type TpSlLabelVariant = 'full' | 'short';

/**
 * Derives the Market/Limit wording from a standalone TP/SL row's `orderType`.
 * `ORDER_TYPE_STOP_LIMIT` covers both user-set Limit and BBO execution (BBO is
 * inferred from "Limit execution + no user-set limit price"), so the chart
 * distinguishes only the two top-level categories. Anything else falls back
 * to Market (pre-feature rows included).
 */
export function getTpSlExecutionLabel(orderType: string | undefined): 'Market' | 'Limit' {
  return orderType === OrderTypeEnum.StopLimit ? 'Limit' : 'Market';
}

/**
 * Builds the chart-marker label text (left half of the line body) for a TP/SL order.
 * Quantity is rendered by the line adapter's separate `setQuantity` box.
 *
 * @param triggerPrice pre-formatted trigger price string (only read in the `full` variant).
 */
export function buildTpSlLabel({
  type,
  triggerPrice,
  executionType,
  variant,
}: {
  type: TpSlType;
  triggerPrice: string;
  executionType: 'Market' | 'Limit';
  variant: TpSlLabelVariant;
}): string {
  if (variant === 'short') return `${type} ${executionType}`;
  return `${type} Trigger ${triggerPrice} / ${executionType}`;
}
